// React imports
import React, {useState, useRef, useEffect, useMemo} from 'react';

// Generic imports
import { useDropzone } from 'react-dropzone';
import _ from 'lodash';
import { parse } from 'csv-parse/browser/esm/sync';
import stringSimilarity from 'string-similarity';

// MUI imports
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import { grey, red } from '@mui/material/colors';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight, faXmark, faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { faFile, faFileJpg, faFilePdf, faFileWord, faFileExcel, faFilePowerpoint, faFileCsv, faCircleQuestion } from '@fortawesome/pro-light-svg-icons';

function AccountsMainImport(props) {
    const { accounts, open, onClose } = props;
    const [ file, setFile ] = useState(null);
    const [ step, setStep ] = useState(0);
    const [ links, setLinks ] = useState({});
    const [ imports, setImports ] = useState(null);
    const [ actions, setActions ] = useState(null);
    const [ done, setDone ] = useState(false);

    function onNext() {
        switch (step) {
            case 0: setStep(1); break;
            case 1: setStep(2); break;
            case 2: setStep(3); break;
            case 3: onClose(); break;
        }
    }

    function onBack() {
        if (step === 1) {
            setStep(0);
        } else if (step === 2) {
            setImports(null);
            setActions(null);
            setStep(1);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
            <Box sx={{ display: 'flex', alignItems: 'center', padding: '24px' }}>
                <FontAwesomeIcon icon={faFileExcel} style={{ fontSize: '22px' }} />
                &nbsp;
                <FontAwesomeIcon icon={faCaretRight} />
                &nbsp;
                <img src={'https://app.monada.ai/favicon.ico'} alt='Monada Logo' style={{ width: '20px', height: '20px' }} />
                &nbsp; &nbsp;
                <Typography variant='h6' sx={{ flex: 1 }}>
                    Import Spreadsheet to Monada
                </Typography>
                <IconButton color='error' onClick={onClose}>
                    <FontAwesomeIcon icon={faXmark} />
                </IconButton>
            </Box>
            { step === 0 && <Step0 file={file} setFile={setFile} /> }
            { step === 1 && <Step1 data={file.data} links={links} setLinks={setLinks} /> }
            { step === 2 && <Step2 accounts={accounts} data={file.data} links={links} imports={imports} setImports={setImports} accounts={accounts} actions={actions} setActions={setActions} /> }
            { step === 3 && <Step3 accounts={accounts} actions={actions} setDone={setDone} /> }
            <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', padding: '36px 12px 12px 12px' }}>
                { step > 0 && step < 3 && (
                    <Button onClick={onBack} variant='outlined' color='primary'>
                        Back
                    </Button>
                )}
                { step > 0 && step < 3 && (<Box sx={{ flex: 1 }} />) }
                <Button onClick={onNext} disabled={
                    (step === 0 && (!file || file.state === 'error')) || 
                    (step === 1 && (!(_.values(links) || []).includes('name'))) ||
                    (step === 3 && (!done))
                } variant='contained' color='primary'>
                    {step === 3 ? 'Done' : step === 2 ? 'Import' : 'Next'}
                </Button> 
            </DialogActions>
        </Dialog>
    );
}

export default AccountsMainImport;

function Step0(props) {

    const { file, setFile } = props;

    const setFileRef = useRef();
    setFileRef.current = setFile;

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: async (acceptedFiles) => {
            setFileRef.current({ state: 'uploading' }); 
            const file = acceptedFiles[0];
            if (!file) return;

            const reader = new FileReader()

            reader.onabort = () => {
                setFileRef.current({ ...file, state: 'error' });
            }

            reader.onerror = () => {
                setFileRef.current({ ...file, state: 'error' });
            }

            reader.onload = () => {
                const content = reader.result

                let data = null;
                try {
                    const workbook = window.XLSX.read(content, {
                        type: 'binary'
                    });

                    workbook.SheetNames.forEach(function(sheetName) {
                        // Here is your object
                        var XL_row_object = window.XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName]);
                        data = XL_row_object;
                    })
                } catch (e) {
                }

                if (!data) {
                    try {
                        data = parse(content, { columns: true });
                        if (!data) {
                            setFileRef.current({ ...file, state: 'error' });
                            return;
                        }
                    } catch (e) {
                        setFileRef.current({ ...file, state: 'error' });
                        return;
                    }
                }

                setFileRef.current({ ...file, state: 'success', data });
            }
            reader.readAsBinaryString(file)
        },
    });


    return (
        <>
            <Typography variant='body1' component={Box} sx={{ padding: '0px 24px 24px 24px' }} >
                Import data from an Excel spreadsheet, or any CSV file, to Monada. This will allow you to quickly and easily import your data into Monada.
            </Typography>
            {(!file || file.state === 'uploading') && (
                <Box sx={{ margin: '12px 36px', padding: '24px', textAlign: 'center', border: `1px dashed ${grey[300]}`, cursor: 'pointer' }}>
                    {file === null && (
                        <div {...getRootProps()}>
                            <FontAwesomeIcon style={{ fontSize: '48px' }} icon={faCloudArrowUp} />
                            <input {...getInputProps()} />
                            <Typography variant='caption' component={Box} >Drag and drop files here or click to browse.</Typography>
                        </div>
                    )}
                    {file?.state === 'uploading' && (
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress size={18} />
                        </Box>
                    )}
                </Box>
            )}
            {(file?.data || file?.state === 'error') && (
                <Box sx={{ display: 'flex', alignItems: 'center', margin: '0px 50px 0px 50px', padding: '6px 12px', border: `1px dashed ${grey[300]}` }} >
                    <FontAwesomeIcon icon={fileToIcon(file.path)} style={{ fontSize: '48px', marginRight: '12px' }} />
                    <Typography variant='caption' component={Box} sx={{ flex: 1 }}>
                        {file.path.substr(0, 50)}{file.path.length > 50 ? '...' : ''}
                    </Typography>
                    <IconButton color='error' onClick={() => setFile(null)}>
                        <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                </Box>
            )}
            {file?.state === 'error' && (
                <Typography variant='caption' component={Box} sx={{ padding: '0px 56px 0px 56px', color: red[500] }} >
                    Error with uploaded file.
                </Typography>
            )}
        </>
    );
}

function Step1(props) {
    const { data, links, setLinks } = props;
    const fields = _(data).map(d => _.keys(d)).flatten().uniq().value();
    return (
        <>
            <Typography variant='body1' component={Box} sx={{ padding: '0px 24px 24px 24px' }} >
                Connect the fields from your spreadsheet to the fields in Monada.
            </Typography>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Field in Spreadsheet</TableCell>
                            <TableCell />
                            <TableCell>Field in Monada</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fields.map((row, i) => (
                            <TableRow key={i}>
                                <TableCell>
                                    {row}
                                </TableCell>
                                <TableCell>
                                    <FontAwesomeIcon icon={faCaretRight} />
                                </TableCell>
                                <TableCell>
                                    <Step1DropDown value={links[row]} setValue={v => setLinks(l => ({...l, [row]: v }))} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

function Step1DropDown(props) {
    const { value, setValue } = props;

    function handleChange(event) {
        setValue(event.target.value);
    }
    return (
        <FormControl fullWidth>
            <Select
                value={value}
                onChange={handleChange}
            >
                {FIELDS.map(f => (
                    <MenuItem key={f.id} value={f.id}>{f.title}</MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

function Step2(props) {
    const { accounts, data, links, imports, setImports, actions, setActions } = props;

    useEffect(() => {
        if (!data || !links) return;

        const _imports = data.map(d => {
            const _import = {};
            const fields = _.values(links);
            fields.forEach(field => {
                const columns = _.keys(links).filter(k => links[k] === field);
                _import[field] = columns.map(c => d[c]).join(' ');
            });
            return _import;
        });

        const cleanImports = _.filter(_imports, i => !!i.name);
        const uniqueImports = _.uniqBy(cleanImports, a => a.name);
        setImports(uniqueImports);
    }, [data, links]);

    const issues = useMemo(() => {
        const accountNames = accounts.map(a => a.name.toLowerCase());
        return (imports || []).map(i => {
            const similar = stringSimilarity.findBestMatch(i.name.toLowerCase(), accountNames);
            return similar.bestMatch.rating > 0.6 ? accounts.find(ac => ac.name.toLowerCase() === similar.bestMatch.target) : null;
        });
    }, [ imports ]);

    useEffect(() => {
        if (!issues) return;

        const _actions = issues.map((issue, i) => {
            if (!issue) return 'add';
            return 'skip';
        });
        setActions(_actions);
    }, [ issues ]);

    if (_.isEmpty(imports) || _.isEmpty(actions)) return;

    return (
        <>
            <Typography variant='body1' component={Box} sx={{ padding: '0px 24px 24px 24px' }} >
                Ensure all accounts are correct before importing.
            </Typography>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Company</TableCell>
                            <TableCell></TableCell>
                            <TableCell sx={{ minWidth: '300px' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {imports.map((row, i) => issues[i] ? row : null).map((row, i) => !row ? null : (
                            <Step2Row
                                row={row}
                                issue={issues[i]}
                                action={actions[i]}
                                setAction={(v) => setActions(ac => ac.map((a, i2) => i2 === i ? v : a))} 
                            />
                        ))}
                        {imports.map((row, i) => !issues[i] ? row : null).map((row, i) => !row ? null : (
                            <Step2Row 
                                row={row} 
                                issue={issues[i]} 
                                action={actions[i]} 
                                setAction={(v) => setActions(ac => ac.map((a, i2) => i2 === i ? v : a))} 
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

function Step2Row(props) {
    const { row, issue,  action, setAction } = props;
    return (
        <TableRow key={row.name}>
            <TableCell>
                <Tooltip title={(
                    <Typography variant='caption' component={Box} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        {_.keys(row).map(k => (<span><b>{FIELDS.find(f => f.id === k).title}</b>: {row[k]} &nbsp;</span>))}
                    </Typography>
                )}>
                    <Typography variant='body1' component={Box} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        {row.name}
                        <FontAwesomeIcon icon={faCircleQuestion} style={{ marginLeft: '3px', fontSize: '13px', position: 'relative', top: '-3px' }} />
                    </Typography>
                </Tooltip>
            </TableCell>
            <TableCell>
                {issue && (
                    <Alert severity='error'>
                        Found similiar account! <a href={`./accounts/${issue.id}/details`} target='_blank'>{issue.name}</a>
                    </Alert>
                )}
            </TableCell>
            <TableCell>
                {!issue && (
                    <Box>
                        Add as new account.
                    </Box>
                )}
                {issue && (
                    <Select value={action} onChange={(e) => setAction(e.target.value)}>
                        <MenuItem value='skip'>Skip</MenuItem>
                        <MenuItem value='update'>Update existing account</MenuItem>
                        <MenuItem value='add'>Add as new account</MenuItem>
                    </Select>
                )}
            </TableCell>
        </TableRow>
    );
}

function Step3(props) {
    const { setDone } = props;
    const [ progress, setProgress ] = useState(0);
    const progressInterval = useRef(0);
    const _setDone = useRef(setDone);

    useEffect(() => {
        progressInterval.current = setInterval(() => {
            setProgress(p => {
                if (p >= 100) {
                    clearInterval(progressInterval.current);
                    _setDone.current(true);
                }
                return Math.min(100, p + Math.random() * 10)
            });
        }, 1000);
    }, []);

    return (
        <>
            <Typography variant='body1' component={Box} sx={{ padding: '0px 24px 24px 24px' }} >
                Uploading accounts to Monada.
            </Typography>
            <Box sx={{ width: '100%', padding: '0px 56px 0px 56px' }}>
                <LinearProgress variant='determinate' value={progress} color={progress < 100 ? 'inherit' : 'success'}/>
            </Box>
        </>
    );
}

function fileToIcon(filename) {
    return {
        'csv': faFileCsv,
        'jpg': faFileJpg,
        'jpeg': faFileJpg,
        'pdf': faFilePdf,
        'doc': faFileWord,
        'docx': faFileWord,
        'xls': faFileExcel,
        'xlsx': faFileExcel,
        'ppt': faFilePowerpoint,
        'pptx': faFilePowerpoint,
    }[filename.split('.').pop().toLowerCase()] || faFile;
}

const FIELDS = [
    { id: 'name', title: 'Company name' },
    { id: 'contact.firstname', title: 'Contact first name' },
    { id: 'contact.lastname', title: 'Contact last name' },
    { id: 'contact.title', title: 'Contact title' },
    { id: 'contact.email', title: 'Contact email' },
    { id: 'address.text', title: 'Contact address' },
    { id: 'metadata.industry', title: 'Industry' },
    { id: 'metadata.origin', title: 'Origin' },
    { id: 'metadata.destination', title: 'Destination' },
    { id: 'metadata.commodity', title: 'Commodity' },
    { id: 'metadata.transportationMethod', title: 'Transport mode' },
    { id: 'metadata.product.type', title: 'Item' },
    { id: 'metadata.product.quantity', title: 'Quantity' },
    { id: 'metadata.product.weight', title: 'Weight' },
    { id: 'metadata.product.volume', title: 'Volume' },
];
